import { MessageDto } from '@act/shared/data-transfer-objects';
import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { messagesRequests } from '../requests/messages.requests';
import { useAppSelector } from '../store-hooks';
import { MessagesState } from '../types/messageTypes';
import { createApiThunk } from '../utils/api-thunk';
import { ActivitySelectorParams } from '../types/selector-types';
import { getGuideInteractions } from './docketSlice';
import { feedSlice, getFeedItems } from './feedSlice';
import { resetRedux } from '../actions/shared-actions';

// INITIAL STATE ===============================================================
const messagesAdapter = createEntityAdapter<MessageDto>();
const initialState: MessagesState = {
  messages: messagesAdapter.getInitialState({ status: 'initial' }),
};

// THUNKS/ACTIONS ==============================================================
export const createMessage = createApiThunk(messagesRequests.createMessage);
export const updateMessage = createApiThunk(messagesRequests.updateMessage);
export const updateMessageNoFollowUpNeeded = createApiThunk(
  messagesRequests.updateMessageNoFollowUpNeeded
);
export const getScheduledMessagesByMember = createApiThunk(
  messagesRequests.getScheduledMessagesByMember
);
export const deleteMessage = createApiThunk(messagesRequests.deleteMessage);
export const sendScheduledMessage = createApiThunk(
  messagesRequests.sendScheduledMessage
);
export const createMessageSuccess = createAction<MessageDto>(
  'messages/createMessageSuccess'
);
export const updateMessageSuccess = createAction<MessageDto>(
  'messages/updateMessageSuccess'
);
export const getSchedulingStatus = createApiThunk(
  messagesRequests.getSchedulingStatus
);

// SLICE =======================================================================
export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    removeMessageFromState: (state, action: { payload: number }) => {
      messagesAdapter.removeOne(state.messages, action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        messagesAdapter.addMany(state.messages, action.payload.messages);
        state.messages.status = 'success';
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.messages) return;

        messagesAdapter.upsertMany(
          state.messages,
          action.payload.feedItemModelView.messages
        );
      })
      .addCase(createMessageSuccess, (state, action) => {
        if (action.payload.sendAt) {
          messagesAdapter.addOne(state.messages, action.payload);
        }
        // messagesAdapter.upsertOne(state.messages, action.payload);
        state.messages.status = 'success';
      })
      .addCase(updateMessageSuccess, (state, action) => {
        messagesAdapter.updateOne(state.messages, {
          id: action.payload.id,
          changes: action.payload,
        });
        state.messages.status = 'success';
      })
      .addCase(getScheduledMessagesByMember.fulfilled, (state, action) => {
        messagesAdapter.upsertMany(state.messages, action.payload);
      })
      // Docket Interactions
      .addCase(getGuideInteractions.fulfilled, (state, action) => {
        messagesAdapter.upsertMany(state.messages, action.payload.messages);
      })
      // Create Message (both immediate and scheduled)
      .addCase(createMessage.pending, state => {
        state.messages.status = 'loading';
      })
      .addCase(createMessage.fulfilled, (state, action) => {
        // Add the new message to the state
        // action.payload is MessageWithFeedItemDto, which extends MessageDto
        if (action.payload.sendAt) {
          messagesAdapter.addOne(state.messages, action.payload);
        }
        state.messages.status = 'success';
      })
      .addCase(createMessage.rejected, state => {
        state.messages.status = 'failed';
      })
      // Update Message
      .addCase(updateMessage.pending, state => {
        state.messages.status = 'loading';
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        // Update the message in the state
        messagesAdapter.updateOne(state.messages, {
          id: action.meta.arg.params.messageId,
          changes: action.payload,
        });
        state.messages.status = 'success';
      })
      .addCase(updateMessage.rejected, state => {
        state.messages.status = 'failed';
      })
      // Delete Message
      .addCase(deleteMessage.pending, state => {
        state.messages.status = 'loading';
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        // Remove the deleted message from the state
        messagesAdapter.removeOne(
          state.messages,
          action.meta.arg.params.messageId
        );
        state.messages.status = 'success';
      })
      .addCase(deleteMessage.rejected, state => {
        state.messages.status = 'failed';
      })
      // Send Scheduled Message
      .addCase(sendScheduledMessage.pending, state => {
        state.messages.status = 'loading';
      })
      .addCase(sendScheduledMessage.fulfilled, (state, action) => {
        messagesAdapter.upsertOne(state.messages, action.payload);
        state.messages.status = 'success';
      })
      .addCase(sendScheduledMessage.rejected, state => {
        state.messages.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const messagesReducer = messagesSlice.reducer;
export const { removeMessageFromState } = messagesSlice.actions;

// SELECTORS ===================================================================
const selectMessageSliceState = state =>
  (state as { messages: typeof initialState })?.messages;
const selectMessagesState = createSelector(
  selectMessageSliceState,
  s => s.messages
);

export const selectScheduledMessagesByMember = (memberId: string) =>
  createSelector(selectMessages, messages => {
    if (!memberId) {
      return [];
    }
    return messages.filter(
      m => m.subjectId === memberId && !m.sentAt && m.sendAt
    );
  });

const { selectAll: selectMessages, selectEntities: selectMessagesMap } =
  messagesAdapter.getSelectors(selectMessagesState);

export const selectMessageById = (id: number) =>
  createSelector(selectMessagesMap, callOutcomes => {
    return callOutcomes[id];
  });

export const selectMessagesByGuideAndTime: (
  state,
  paramObj: ActivitySelectorParams
) => MessageDto[] = createSelector(
  [
    selectMessages,
    (state, paramObj: ActivitySelectorParams) => paramObj.guideId,
    (state, paramObj: ActivitySelectorParams) => paramObj.startEpochTime,
    (state, paramObj: ActivitySelectorParams) => paramObj.endEpochTime,
  ],
  (messages, guideId, startEpochTime, endEpochTime) => {
    return messages.filter(
      m =>
        m.userId === guideId &&
        // Only include messages that have actually been sent
        m.sentAt !== null &&
        // Only include messages that were sent within the time range
        m.sentAt >= startEpochTime &&
        m.sentAt <= endEpochTime
    );
  }
);

// EXPORT HELPER ===============================================================
export const messagesAppSelectors = {
  useSelectMessageById: id => useAppSelector(selectMessageById(id)),
  // useSelectMessages: () => useAppSelector(selectMessages),
  useSelectScheduledMessagesByMember: (memberId: string) =>
    useAppSelector(selectScheduledMessagesByMember(memberId)),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
